import React, { useMemo, useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { PORTFOLIO, COMPANY } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import SmartImage from "../components/SmartImage";
import { track } from "../lib/analytics";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const project = useMemo(() => PORTFOLIO.find((p) => p.id === id), [id]);
  const { prev, next } = useMemo(() => {
    const idx = PORTFOLIO.findIndex((p) => p.id === id);
    if (idx === -1) return { prev: null, next: null };
    return {
      prev: PORTFOLIO[(idx - 1 + PORTFOLIO.length) % PORTFOLIO.length],
      next: PORTFOLIO[(idx + 1) % PORTFOLIO.length],
    };
  }, [id]);

  // Lightbox state
  const [lightbox, setLightbox] = useState(null); // image index or null
  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % project.gallery.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + project.gallery.length) % project.gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, project]);

  usePageMeta({
    title: project
      ? `${project.title} | Interior Fit-Out Project ${project.location || "Dubai"} — Time Furniture`
      : "Project | Time Furniture",
    description: project
      ? `${project.title}. ${(project.scope || []).slice(0, 4).join(", ")}. Interior fit-out project in ${project.location || "UAE"} by Time Furniture, Dubai.`
      : undefined,
    canonical: project
      ? `https://timefurniture.ae/portfolio/${project.id}`
      : undefined,
    ogImage: project ? project.image : undefined,
    jsonLd: project
      ? {
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: project.title,
          description: project.description,
          url: `https://timefurniture.ae/portfolio/${project.id}`,
          image: (project.gallery || [project.image]).map((g) =>
            g.startsWith("http")
              ? g
              : `https://timefurniture.ae${g}`
          ),
          locationCreated: {
            "@type": "Place",
            name: project.location || "United Arab Emirates",
          },
          dateCreated: String(project.year || ""),
          creator: { "@id": "https://timefurniture.ae/#organization" },
          about: (project.scope || []).map((s) => ({
            "@type": "Service",
            name: s,
          })),
        }
      : undefined,
  });

  if (!project) {
    return (
      <div data-testid="project-not-found" className="bg-[#FAF5EC] min-h-[60vh] flex items-center justify-center px-6">
        <div className="text-center">
          <p className="eyebrow mb-4">Project Not Found</p>
          <h1 className="font-serif-display text-4xl md:text-5xl text-[#1C110C] mb-8">
            We couldn't find that project.
          </h1>
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-10 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#56151F] transition-colors"
          >
            <ArrowLeft size={14} /> Back to Portfolio
          </Link>
        </div>
      </div>
    );
  }

  const gallery = project.gallery && project.gallery.length > 0 ? project.gallery : [project.image];

  return (
    <div data-testid={`project-page-${project.id}`} className="bg-[#FAF5EC]">
      {/* ========== CINEMATIC HERO ========== */}
      <section className="relative h-[92vh] min-h-[680px] -mt-20 overflow-hidden">
        <img
          src={gallery[0]}
          alt={project.title}
          className="absolute inset-0 w-full h-full object-cover scale-105 animate-fade-in"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/25 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF5EC] via-transparent to-transparent" />

        {/* Back button */}
        <div className="absolute top-24 md:top-28 left-6 md:left-12 z-20">
          <button
            onClick={() => navigate(-1)}
            data-testid="project-back-btn"
            className="inline-flex items-center gap-3 text-[11px] tracking-[0.36em] uppercase text-white bg-black/40 backdrop-blur-sm px-5 py-3 border border-white/25 hover:bg-black/60 hover:gap-4 transition-all"
          >
            <ArrowLeft size={14} /> Back
          </button>
        </div>

        {/* Centered title stack */}
        <div className="absolute inset-0 flex items-end pb-24 md:pb-36">
          <div className="w-full max-w-[1500px] mx-auto px-6 md:px-12 text-center animate-fade-up">
            <p className="mb-6 text-[11px] md:text-[12px] tracking-[0.44em] uppercase text-[#D4A963]">
              {project.category} · {project.year}
              {project.location ? ` · ${project.location}` : ""}
            </p>
            <h1 className="font-serif-display font-semibold text-white text-5xl sm:text-6xl md:text-7xl lg:text-[104px] xl:text-[120px] leading-[0.95] tracking-[-0.02em] drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)] max-w-6xl mx-auto">
              {project.title}
            </h1>
          </div>
        </div>

        {/* Bottom meta strip */}
        <div className="absolute inset-x-0 bottom-0 z-10 border-t border-white/15 bg-black/30 backdrop-blur-md">
          <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-white/90 text-[10px] md:text-[11px] tracking-[0.32em] uppercase">
            <span>{project.category}</span>
            <span className="hidden md:inline text-[#D4A963]">·</span>
            <span>{project.year}</span>
            <span className="hidden md:inline text-[#D4A963]">·</span>
            <span>{project.location || "United Arab Emirates"}</span>
            <span className="hidden md:inline text-[#D4A963]">·</span>
            <span>{(project.scope || []).length} Disciplines</span>
          </div>
        </div>
      </section>

      {/* ========== BRIEF · SCOPE ========== */}
      <Breadcrumbs
        testid="project-breadcrumbs"
        items={[
          { label: "Home", to: "/" },
          { label: "Portfolio", to: "/portfolio" },
          { label: project.title },
        ]}
      />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-28 md:pb-40 lg:pb-48">
        {/* Compact fact grid — only fields that genuinely exist on the project */}
        <dl
          data-testid="project-fact-grid"
          className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#6E1F2C]/10 border border-[#6E1F2C]/10 mb-16 md:mb-20"
        >
          {[
            { k: "Location", v: project.location || "United Arab Emirates" },
            { k: "Year", v: String(project.year || "—") },
            { k: "Project type", v: project.category || "Interior" },
            { k: "Disciplines", v: `${(project.scope || []).length} areas` },
          ].map((f) => (
            <div key={f.k} className="bg-[#FAF5EC] px-5 py-6" data-testid={`project-fact-${f.k.toLowerCase().replace(/\s+/g, "-")}`}>
              <dt className="text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F]">{f.k}</dt>
              <dd className="mt-2 font-serif-display text-lg md:text-xl text-[#1C110C] leading-snug">{f.v}</dd>
            </div>
          ))}
        </dl>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">The Brief</p>
            <p className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.2] tracking-tight text-[#1C110C] max-w-3xl">
              {project.description}
            </p>
          </div>
          <div className="lg:col-span-5 lg:pl-12 lg:border-l lg:border-[#6E1F2C]/20">
            <p className="eyebrow mb-8">Scope of Work</p>
            <div className="flex flex-wrap gap-3">
              {(project.scope || []).map((s) => (
                <span
                  key={s}
                  data-testid={`project-scope-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex items-center gap-2 px-5 py-3 border border-[#6E1F2C]/30 text-[#1C110C] text-sm bg-white/60 hover:bg-[#6E1F2C] hover:text-white hover:border-[#6E1F2C] transition-colors"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== PDF VIEWER (when project has a PDF brochure / case-study doc) ========== */}
      {project.pdf && (
        <section
          data-testid="project-pdf-viewer"
          className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <p className="eyebrow mb-5">Case Study</p>
                <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight text-[#1C110C] leading-[1.05]">
                  Project <em className="text-[#6E1F2C] not-italic">document</em>.
                </h2>
                <p className="mt-4 text-[#7A6B5F] text-sm md:text-base max-w-xl">
                  Scroll through the full project deck below — or open it in a new tab.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href={project.pdf}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="project-pdf-open-tab"
                  className="inline-flex items-center gap-2 border border-[#6E1F2C]/30 text-[#1C110C] px-5 py-3 text-[11px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-white hover:border-[#6E1F2C] transition-colors"
                >
                  Open in new tab ↗
                </a>
                <Link
                  to="/portfolio"
                  data-testid="project-pdf-back-portfolio"
                  className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#6E1F2C] px-5 py-3 hover:text-[#56151F] transition-colors"
                >
                  <ArrowLeft size={14} /> Back to Portfolio
                </Link>
              </div>
            </div>

            {/* Scrollable PDF — uses browser-native renderer; no auto-download */}
            <div className="bg-[#1C110C] border border-[#6E1F2C]/25 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.35)]">
              <iframe
                src={`${project.pdf}#toolbar=1&navpanes=0&view=FitH`}
                title={`${project.title} — project document`}
                data-testid="project-pdf-iframe"
                className="w-full h-[80vh] md:h-[90vh] bg-[#1C110C]"
                loading="lazy"
              />
            </div>
            <p className="mt-4 text-center text-xs text-[#7A6B5F] italic">
              If the document does not load,{" "}
              <a
                href={project.pdf}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-4 hover:text-[#6E1F2C]"
              >
                open it directly in a new tab
              </a>
              .
            </p>
          </div>
        </section>
      )}

      {/* ========== VIDEO WALKTHROUGH (only if project.video provided) ========== */}
      {project.video && (
        <section
          data-testid="project-video-section"
          className="bg-[#1C110C] py-24 md:py-32"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
              <p className="eyebrow text-[#D4A963] mb-6">On-Site Walkthrough</p>
              <h2 className="font-serif-display text-3xl md:text-5xl lg:text-6xl tracking-tight text-white leading-[1.05]">
                See it <em className="text-[#D4A963] not-italic">in motion</em>.
              </h2>
            </div>
            <div className="relative aspect-video bg-black overflow-hidden border border-[#D4A963]/20">
              <video
                data-testid="project-video"
                src={project.video}
                poster={project.videoPoster}
                controls
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>
      )}

      {/* ========== EDITORIAL GALLERY (skipped for PDF-only projects) ========== */}
      {!project.pdf && gallery.length > 1 && (
        <section className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15 py-24 md:py-36 lg:py-44">
          <div className="max-w-[1720px] mx-auto px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto mb-20 md:mb-28">
              <p className="eyebrow mb-6">Gallery</p>
              <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl tracking-tight text-[#1C110C] leading-[1.02]">
                In <em className="text-[#6E1F2C] not-italic">detail</em>.
              </h2>
            </div>

            {/* Editorial stacked gallery — full-bleed big + 2-up rows */}
            <div className="space-y-6 md:space-y-10">
              {gallery.slice(1).map((img, i) => {
                const isSingle = i % 3 === 0;
                if (isSingle) {
                  return (
                    <button
                      key={i}
                      data-testid={`project-gallery-${i + 1}`}
                      onClick={() => setLightbox(i + 1)}
                      className="group block w-full overflow-hidden h-[520px] md:h-[760px] lg:h-[880px] bg-[#1C110C] img-zoom-parent"
                      aria-label={`Open image ${i + 2}`}
                    >
                      <SmartImage
                        src={img}
                        alt={`${project.title} — ${i + 2}`}
                        className="img-zoom"
                      />
                    </button>
                  );
                }
                // otherwise render the next two as a pair
                return null;
              })}

              {/* Pair rows — for every set of 3 after the first single, next 2 form a pair */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                {gallery.slice(1).map((img, i) => {
                  if (i % 3 === 0) return null; // skip singles (rendered above)
                  return (
                    <button
                      key={`pair-${i}`}
                      data-testid={`project-gallery-pair-${i + 1}`}
                      onClick={() => setLightbox(i + 1)}
                      className="group block overflow-hidden h-[420px] md:h-[580px] lg:h-[680px] bg-[#1C110C] img-zoom-parent"
                      aria-label={`Open image ${i + 2}`}
                    >
                      <SmartImage
                        src={img}
                        alt={`${project.title} — ${i + 2}`}
                        className="img-zoom"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ========== CTA BAND ========== */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-28 md:py-40 text-center">
        <p className="eyebrow mb-6">Start Your Project</p>
        <h2 className="font-serif-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-[#1C110C]">
          Inspired? Let's build <br className="hidden md:block" />
          something <em className="text-[#6E1F2C] not-italic">similar</em>.
        </h2>
        <p className="mt-10 max-w-xl mx-auto text-[#3E2A22] leading-relaxed text-lg">
          Tell us about your space — a senior member of our team will respond within 24 hours.
        </p>
        <div className="mt-14 flex flex-wrap gap-5 justify-center">
          <Link
            to="/contact"
            data-testid="project-cta-contact"
            onClick={() => track.siteVisitCta("project_detail")}
            className="inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-11 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#56151F] transition-all shadow-[0_20px_40px_-10px_rgba(110,31,44,0.55)]"
          >
            Book a Site Visit <ArrowRight size={15} />
          </Link>
          <a
            href={`https://wa.me/${COMPANY.whatsapp}?text=${COMPANY.whatsappFitOutText}`}
            target="_blank"
            rel="noreferrer"
            data-testid="project-cta-whatsapp"
            className="inline-flex items-center gap-3 border border-[#6E1F2C] text-[#6E1F2C] px-11 py-5 text-[11px] tracking-[0.32em] uppercase font-medium hover:bg-[#6E1F2C] hover:text-white transition-all"
          >
            <MessageCircle size={16} strokeWidth={2.2} /> Request a Similar Project
          </a>
        </div>
      </section>

      {/* ========== PREV / NEXT ========== */}
      <section className="border-t border-[#6E1F2C]/15 bg-[#F2E9D8]">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-14 md:py-20 grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="md:justify-self-start">
            {prev && (
              <Link
                to={`/portfolio/${prev.id}`}
                data-testid="project-prev"
                className="group block"
              >
                <span className="block text-[10px] tracking-[0.32em] uppercase text-[#7A6B5F] mb-2 flex items-center gap-2">
                  <ArrowLeft size={12} className="group-hover:-translate-x-1 transition-transform" /> Previous Project
                </span>
                <span className="font-serif-display text-2xl md:text-3xl text-[#1C110C] group-hover:text-[#6E1F2C] transition-colors">
                  {prev.title}
                </span>
              </Link>
            )}
          </div>

          <div className="md:justify-self-center">
            <Link
              to="/portfolio"
              data-testid="project-all-link"
              className="inline-flex items-center gap-3 border border-[#6E1F2C] text-[#6E1F2C] px-8 py-4 text-[11px] tracking-[0.32em] uppercase hover:bg-[#6E1F2C] hover:text-white transition-all"
            >
              All Projects
            </Link>
          </div>

          <div className="md:justify-self-end text-left md:text-right">
            {next && (
              <Link
                to={`/portfolio/${next.id}`}
                data-testid="project-next"
                className="group block"
              >
                <span className="block text-[10px] tracking-[0.32em] uppercase text-[#7A6B5F] mb-2 flex items-center md:justify-end gap-2">
                  Next Project <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                </span>
                <span className="font-serif-display text-2xl md:text-3xl text-[#1C110C] group-hover:text-[#6E1F2C] transition-colors">
                  {next.title}
                </span>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ========== LIGHTBOX ========== */}
      {lightbox !== null && (
        <div
          data-testid="project-lightbox"
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center animate-fade-in p-6"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-6 right-6 text-white text-[11px] tracking-[0.32em] uppercase hover:text-[#D4A963]"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            Close ✕
          </button>
          <button
            className="absolute left-6 top-1/2 -translate-y-1/2 text-white bg-white/10 p-3 hover:bg-white/25"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i - 1 + gallery.length) % gallery.length);
            }}
            aria-label="Previous"
          >
            <ChevronLeft size={24} />
          </button>
          <img loading="lazy"
            src={gallery[lightbox]}
            alt=""
            className="max-w-[92vw] max-h-[88vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute right-6 top-1/2 -translate-y-1/2 text-white bg-white/10 p-3 hover:bg-white/25"
            onClick={(e) => {
              e.stopPropagation();
              setLightbox((i) => (i + 1) % gallery.length);
            }}
            aria-label="Next"
          >
            <ChevronRight size={24} />
          </button>
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-[10px] tracking-[0.32em] uppercase">
            {lightbox + 1} / {gallery.length}
          </p>
        </div>
      )}
    </div>
  );
}
